import { useDataProvider, useGetIdentity } from "ra-core";
import { useCallback } from "react";
import { supabase } from "../providers/supabase/supabase";

export type ActivityImportSchema = {
  type: string;
  text: string;
  company_name: string;
  contact_name: string;
  priority: string;
  status: string;
  due_date: string;
};

export function useActivityImport() {
  const dataProvider = useDataProvider();
  const user = useGetIdentity();

  const processBatch = useCallback(
    async (batch: ActivityImportSchema[]) => {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) throw new Error("Not authenticated");

      const { data: sale } = await supabase
        .from("sales")
        .select("org_id")
        .eq("user_id", authUser.id)
        .maybeSingle();

      const orgId = sale?.org_id;
      if (!orgId) throw new Error("No org_id found");

      // Fetch maps
      const { data: companies } = await supabase
        .from("companies")
        .select("id, name")
        .eq("org_id", orgId);
      const { data: contacts } = await supabase
        .from("contacts")
        .select("id, first_name, last_name")
        .eq("org_id", orgId);

      const companyMap = new Map(
        companies?.map((c) => [c.name.trim().toLowerCase(), c.id]) || [],
      );
      const contactMap = new Map(
        contacts?.map((c) => [
          `${c.first_name} ${c.last_name}`.trim().toLowerCase(),
          c.id,
        ]) || [],
      );

      await Promise.all(
        batch.map(async (row) => {
          if (!row.text?.trim()) return;

          const companyId = row.company_name
            ? companyMap.get(row.company_name.trim().toLowerCase())
            : undefined;

          const contactId = row.contact_name
            ? contactMap.get(row.contact_name.trim().toLowerCase())
            : undefined;

          await dataProvider.create("crm_activities", {
            data: {
              type: row.type === "task" ? "task" : "note",
              text: row.text,
              priority: row.priority,
              status: row.status,
              due_date: row.due_date
                ? new Date(row.due_date).toISOString()
                : undefined,
              company_id: companyId,
              contact_id: contactId,
              sales_id: user?.identity?.id,
              org_id: orgId,
            },
          });
        }),
      );
    },
    [dataProvider, user?.identity?.id],
  );

  return processBatch;
}
